import { useTranslation } from 'react-i18next'

const GREEN = '#2ac47e'
const YELLOW = '#ffb800'
const RED = '#ff675e'
const MUTED = '#888'

function understandingColor(value: number | null, muted: boolean): string {
  if (muted || value == null) return MUTED
  if (value >= 4) return GREEN
  if (value === 3) return YELLOW
  return RED
}

export function correctRateColor(value: number | null): string {
  if (value == null) return MUTED
  if (value >= 60) return GREEN
  if (value >= 50) return YELLOW
  return RED
}

export function UnderstandingMetric({
  value,
  muted = false,
  align = 'center',
}: {
  value: number | null
  muted?: boolean
  align?: 'start' | 'center'
}) {
  const { t } = useTranslation('coaching')
  const color = understandingColor(value, muted)

  return (
    <span className={`flex flex-col gap-1 ${align === 'center' ? 'items-center' : 'items-start'}`}>
      <span className="whitespace-nowrap text-[11px] font-semibold" style={{ color }}>
        {value == null
          ? t('chapterReport.missing')
          : t(`chapterReport.understandingLevels.${Math.min(5, Math.max(1, value))}`)}
      </span>
      <span className="inline-flex h-1.5 items-center gap-1" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((dot) => (
          <span
            key={dot}
            className="size-1.5 rounded-full"
            style={{ backgroundColor: value != null && dot <= value ? color : '#d0d0d0' }}
          />
        ))}
      </span>
    </span>
  )
}

export function CorrectRateMetric({
  value,
  compact = false,
}: {
  value: number | null
  compact?: boolean
}) {
  const { t } = useTranslation('coaching')
  const color = correctRateColor(value)
  const normalized = value == null ? 0 : Math.min(100, Math.max(0, value))

  if (compact) {
    return (
      <strong className="text-[13px]" style={{ color }}>
        {value == null ? t('chapterReport.missing') : `${value}%`}
      </strong>
    )
  }

  return (
    <span className="flex w-[88px] flex-col items-end gap-1">
      <strong className="px-0.5 text-[11px]" style={{ color }}>
        {value == null ? t('chapterReport.missing') : `${value}%`}
      </strong>
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-[#d0d0d0]" aria-hidden="true">
        <span
          className="block h-full rounded-full"
          style={{ width: `${normalized}%`, backgroundColor: color }}
        />
      </span>
    </span>
  )
}

export function ScoreRing({
  value,
  ariaLabel,
}: {
  value: number | null
  ariaLabel: string
}) {
  const normalized = value == null ? 0 : Math.min(100, Math.max(0, value))

  return (
    <div
      className="relative grid size-[72px] shrink-0 place-items-center text-xl font-bold text-[#4599ff]"
      aria-label={ariaLabel}
    >
      <svg
        aria-hidden="true"
        className="absolute inset-0 size-full"
        viewBox="0 0 72 72"
        width="72"
        height="72"
      >
        <circle cx="36" cy="36" r="29" fill="none" stroke="#eaeaea" strokeWidth="8" />
        <circle
          cx="36"
          cy="36"
          r="29"
          fill="none"
          pathLength="100"
          stroke="#4599ff"
          strokeDasharray={`${normalized} 100`}
          strokeLinecap="butt"
          strokeWidth="8"
          transform="rotate(-90 36 36)"
        />
      </svg>
      <span className="relative">{value ?? '—'}</span>
    </div>
  )
}
